<ng-template #usersStats>
  @for (insight of insights(); track $index) {
    <lib-stat
      [class.flex-grow]="!floating()"
      [overline]="insight.label"
      [label]="insight.value"
      [iconSvgName]="getInsightIconName(insight.key)" />
  }
</ng-template>

<ng-template #usersTableTitle>
  @if (!floating()) {
    <h5 class="text-neutral-900 font-semibold text-lg py-lg">
      {{ 'users_database_screen_subtitle' | transloco }}
    </h5>
  }

  <lib-badge
    label="{{ totalCount() }}"
    [badgeClass]="'px-sm border-none text-sm font-medium bg-primary-50 text-primary-600'" />
</ng-template>

<ng-template #usersSearch>
  <lib-search-field
    [searchInputWidth]="searchInputWidth"
    [placeholder]="'search_by_name_placeholder'"
    (searchKeywordEmitter)="onSearchTermChange($event)" />
</ng-template>

<ng-template #tableActionSectionRef>
  <a
    class="flex items-center bg-primary-500 text-white font-medium text-sm py-sm px-2xl border-none rounded-md gap-sm flex items-center"
    [routerLink]="[`${ADMIN_ROUTES.USERS_DATABASE.FORM}` | tenantPath: 'admin']"
    id="users_database_screen_new_user_button">
    <lib-add-icon [iconClass]="'size-5 text-white'" />

    <span class="hidden md:block">
      {{ 'users_database_screen_new_user_button' | transloco }}
    </span>
  </a>
</ng-template>

<ng-template #usersTableArea>
  <lib-table
    [tableClass]="'min-h-[calc(100vh-35.5rem)] h-auto w-full whitespace-nowrap'"
    [id]="'users'"
    [dataSource]="usersWithPortalAccess()"
    [loading]="isLoading()"
    [columns]="allColumns()"
    [viewCount]="totalCount()"
    [noDataTitle]="'table_no_data_title' | transloco"
    [noDataDescription]="'section_no_data_description' | transloco"
    [tableActionSectionRef]="floating() ? null : tableActionSectionRef"
    [searchTemplateRef]="searchInToolbar() ? usersSearch : null"
    [tableFilterTabs]="tableFilterTabs()"
    [lastColumnAlignment]="'justify-end'"
    (handleViewIconClick)="viewUserDetails($event)"
    (handleTableFilterTab)="handleTableFilterTab($event)"
    (handleSortColumn)="handleSortColumn($event)"
    [bodyTabTemplateRef]="
      [
        { template: number },
        { template: name },
        { template: department },
        { template: jobTitle },
        { template: placementType, identifier: 'placement_type' },
        { template: lastAccess },
        { template: roles, identifier: 'roles' },
        { template: status, identifier: 'status' },
        { template: action },
      ]
      | filterTableColByFeatFlag: isUserPlacementTypesEnabled() : 'placement_type'
      | filterTableColByFeatFlag: isEmployeeVendorPortalEnabled() : 'roles'
    "
    [titleBottomBorder]="false">
    <ng-template #number let-row let-i="i">
      {{ (currentPage() - 1) * currentSize() + (i + 1) }}
    </ng-template>

    <ng-template #name let-row>
      <div class="flex items-center gap-md">
        <lib-avatar
          [fullName]="row?.name"
          [img]="row?.image?.url"
          borderRadius="rounded-full"></lib-avatar>
        <span class="text-xs font-medium text-neutral-900">
          {{ row?.name | truncate: 30 }}
        </span>
      </div>
    </ng-template>

    <ng-template #department let-row>
      <div class="flex items-center gap-lg">
        <lib-avatar
          [fullName]="row.placement?.department?.name"
          [img]="row.placement?.department?.image?.url"
          borderRadius="rounded-full"></lib-avatar>
        <span class="text-xs font-medium text-neutral-900">
          {{ row.placement?.department?.name | truncate: 30 }}
        </span>
      </div>
    </ng-template>

    <ng-template #jobTitle let-row>
      {{ row.placement?.job_title?.name | truncate: 30 }}
    </ng-template>

    <ng-template #placementType let-row>
      <span class="text-xs font-medium text-neutral-900">
        {{ row.placement?.type?.label }}
      </span>
    </ng-template>

    <ng-template #lastAccess let-row>
      <span dir="ltr">
        {{ row.last_accessed_at | date: 'dd-MMM-yyyy hh:mm a' }}
      </span>
    </ng-template>

    <ng-template #roles let-row>
      @if (row.portalAccessLabel) {
        <div class="flex flex-wrap items-center gap-sm">
          <lib-badge
            [label]="row.portalAccessLabel | transloco"
            [badgeClass]="'px-sm border-primary-500 font-medium text-primary-500 bg-white'"></lib-badge>
        </div>
      }
    </ng-template>

    <ng-template #status let-row>
      <lib-badge
        [label]="row.state.label"
        [badgeClass]="
          'px-sm border-none text-xs font-medium text-white ' +
          (row.state.value | userStatus)
        "></lib-badge>
    </ng-template>

    <ng-template #action let-row>
      <lib-action-list
        [parentName]="'users'"
        [menuItems]="getStatusActionList(row.state.value, row.id, row.roles)"
        (itemClick)="actionListItemClick($event, row)"></lib-action-list>
    </ng-template>
  </lib-table>

  @if (userList().length) {
    <div class="py-2xl">
      <lib-paginator
        [sizeOptionsLabel]="
          'pagination_number_of_rows_per_page_label' | transloco
        "
        [nextBtnLabel]="'pagination_next_button_label' | transloco"
        [previousBtnLabel]="'pagination_previous_button_label' | transloco"
        [currentPage]="currentPage()"
        [totalPages]="totalPages()"
        [totalItems]="totalCount()"
        [showChangeSize]="true"
        [sizeOptions]="pageSizeOptions"
        (goTo)="handlePagination($event)"
        (next)="handlePagination($event)"
        (previous)="handlePagination($event)"
        (handleChangeSize)="changeSize($event)"
        [currentSize]="currentSize()"></lib-paginator>
    </div>
  }
</ng-template>

@if (isInsightsLoading()) {
  <lib-overlay-loader />
} @else if (floating()) {
  <div class="h-full flex flex-col" [class]="floatingPageClasses()">
    <div class="mb-lg flex items-center justify-between gap-lg">
      <h5 class="text-neutral-900 font-semibold text-2xl">
        {{ 'users_database_screen_title' | transloco }}
      </h5>

      <ng-container [ngTemplateOutlet]="tableActionSectionRef" />
    </div>

    @if (insights().length) {
      <lib-insights-panel>
        <ng-container [ngTemplateOutlet]="usersStats" />
      </lib-insights-panel>
    }

    <section
      class="floating-card p-2xl flex flex-col"
      [class.mt-lg]="insights().length > 0">
      <!-- UI_Version_2: no title or count row; the search is in the table toolbar -->
      @if (!searchInToolbar()) {
        <div class="flex items-center justify-end gap-lg">
          <ng-container [ngTemplateOutlet]="usersSearch" />
        </div>
      }

      <ng-container [ngTemplateOutlet]="usersTableArea" />
    </section>
  </div>
} @else {
  <div class="h-full flex flex-col">
    <div class="flex flex-col gap-2xl">
      <h5 class="text-neutral-900 font-semibold text-2xl">
        {{ 'users_database_screen_title' | transloco }}
      </h5>

      <div class="flex gap-xl">
        <ng-container [ngTemplateOutlet]="usersStats" />
      </div>
    </div>

    <div
      class="flex border-b border-neutral-200 items-center justify-between mt-2xl gap-lg">
      <div class="flex items-center gap-lg">
        <ng-container [ngTemplateOutlet]="usersTableTitle" />
      </div>

      <ng-container [ngTemplateOutlet]="usersSearch" />
    </div>

    <div class="py-lg h-[90%]">
      <ng-container [ngTemplateOutlet]="usersTableArea" />
    </div>
  </div>
}

<ng-template #deactivate>
  <ng-container
    [ngTemplateOutlet]="replaceUser"
    [ngTemplateOutletContext]="{
      $implicit: 'deactivate',
      class: 'destructive',
      header: 'deactivate_user_modal_title' | transloco,
      description: 'deactivate_user_modal_description' | transloco,
      descriptionHighlight:
        'deactivate_user_modal_highlighted_description' | transloco,
      submitBtnLabel:
        'deactivate_user_modal_deactivate_user_button' | transloco,
      submitFn: deactivateUser,
    }" />
</ng-template>

<ng-template #deactivateVendorOnly>
  <div class="flex justify-center">
    <div
      class="flex justify-center items-center bg-destructive-50 p-lg rounded-full mb-xl">
      <lib-close-square-icon
        viewBox="0 0 30 30"
        width="27"
        height="27"
        iconClass="text-destructive-500" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm text-neutral-900">
    {{ 'deactivate_user_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{ 'deactivate_user_modal_description' | transloco }}
    <span class="font-medium text-primary-500 block">
      {{ 'deactivate_user_modal_highlighted_description' | transloco }}
    </span>
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      [class.opacity-30]="isSubmitting()"
      [disabled]="isSubmitting()"
      (click)="deactivateUser()"
      class="bg-destructive-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{ 'deactivate_user_modal_deactivate_user_button' | transloco }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{ 'deactivate_user_modal_no_keep_it_button' | transloco }}
    </button>
  </div>
</ng-template>

<ng-template #activate>
  <div class="flex justify-center">
    <div
      class="flex justify-center items-center bg-primary-50 p-lg rounded-full mb-xl">
      <lib-tick-square-icon
        iconClass="text-primary-500"
        viewBox="0 0 30 30"
        width="27"
        height="27" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm text-neutral-900">
    {{ 'activate_user_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{ 'activate_user_modal_description' | transloco }}
    <span class="font-medium text-primary-500 block">
      {{ 'activate_user_modal_highlighted_description' | transloco }}
    </span>
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      (click)="activateUser()"
      class="bg-primary-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{ 'activate_user_modal_activate_user_button' | transloco }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{ 'activate_user_modal_cancel_button' | transloco }}
    </button>
  </div>
</ng-template>

<ng-template #reinvite>
  <div class="flex justify-center">
    <div
      class="flex justify-center items-center bg-primary-50 p-lg rounded-full mb-xl">
      <lib-sms-icon
        iconClass="text-primary-500"
        viewBox="0 0 30 30"
        width="27"
        height="27" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm">
    {{ 'reinvite_user_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{ 'reinvite_user_modal_description' | transloco }}
    <span class="font-medium text-destructive-500">{{
      'reinvite_user_modal_highlighted_description' | transloco
    }}</span>
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      (click)="reinviteUser()"
      [disabled]="isReinviteUser()"
      class="bg-primary-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{ 'reinvite_user_modal_reinvite_user_button' | transloco }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{ 'reinvite_user_modal_cancel_button' | transloco }}
    </button>
  </div>
</ng-template>

<ng-template #sendInvitation>
  <div class="flex justify-center">
    <div
      class="flex justify-center items-center bg-primary-50 p-lg rounded-full mb-xl">
      <lib-sms-icon
        iconClass="text-primary-500"
        viewBox="0 0 30 30"
        width="27"
        height="27" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm">
    {{ 'users_database_screen_send_invitation_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center">
    {{ 'users_database_screen_send_invitation_modal_description' | transloco }}
  </p>
  <p class="font-medium text-primary-500 mb-4xl text-center">
    {{ selectedUser().email }}
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      (click)="reinviteUser()"
      [disabled]="isReinviteUser()"
      class="bg-primary-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{
        'users_database_screen_send_invitation_modal_send_invitation_button'
          | transloco
      }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{
        'users_database_screen_send_invitation_modal_cancel_button' | transloco
      }}
    </button>
  </div>
</ng-template>

<ng-template #resetpassword>
  <div class="flex justify-center">
    <div
      class="flex justify-center items-center bg-primary-50 p-lg rounded-full mb-xl">
      <lib-lock-icon
        iconClass="text-primary-500"
        viewBox="0 0 30 30"
        width="27"
        height="27" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm">
    {{ 'reset_password_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{ 'reset_password_modal_description' | transloco }}
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      (click)="resetPassword()"
      class="bg-primary-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{ 'reset_password_modal_title' | transloco }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{ 'reset_password_modal_cancel_button' | transloco }}
    </button>
  </div>
</ng-template>

<ng-template #cancelInvitation>
  <ng-container
    [ngTemplateOutlet]="replaceUser"
    [ngTemplateOutletContext]="{
      $implicit: 'cancel_invitation' | transloco,
      class: 'primary',
      header: 'cancel_invitation_modal_title' | transloco,
      description: 'cancel_invitation_modal_description' | transloco,
      submitBtnLabel: 'cancel_invitation_modal_confirm_button' | transloco,
      submitFn: cancelUserInvitation,
    }" />
</ng-template>

<ng-template #deleteRecord>
  <div class="text-center">
    <div
      class="inline-flex justify-center items-center bg-destructive-50 p-sm rounded-full mb-xl w-[4rem] h-[4rem]">
      <lib-delete-icon [iconClass]="'size-8 text-destructive-500'" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm">
    {{ 'delete_user_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{ 'delete_user_modal_description' | transloco }}
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      [class.opacity-30]="isSubmitting()"
      [disabled]="isSubmitting()"
      (click)="deleteUser()"
      class="bg-destructive-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{ 'delete_user_modal_delete_user_button' | transloco }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{ 'delete_user_modal_cancel_button' | transloco }}
    </button>
  </div>
</ng-template>

<ng-template #deleteExpired>
  <ng-container
    [ngTemplateOutlet]="replaceUser"
    [ngTemplateOutletContext]="{
      $implicit: 'delete',
      class: 'destructive',
      header: 'delete_user_modal_title' | transloco,
      description: 'delete_user_modal_description' | transloco,
      submitBtnLabel: 'delete_user_modal_delete_user_button' | transloco,
      submitFn: deleteUser,
    }" />
</ng-template>

<ng-template #removeEmployeeRole>
  <ng-container
    [ngTemplateOutlet]="replaceUser"
    [ngTemplateOutletContext]="{
      $implicit: REPLACE_USER_MODAL_MODE.REMOVE_EMPLOYEE_ROLE,
      class: 'destructive',
      header:
        'users_database_screen_remove_employee_role_modal_title' | transloco,
      description:
        'users_database_screen_remove_employee_role_modal_description'
        | transloco,
      descriptionHighlight:
        'users_database_screen_remove_employee_role_modal_highlighted_description'
        | transloco,
      submitBtnLabel:
        'users_database_screen_remove_employee_role_modal_confirm_button'
        | transloco,
      submitFn: removeEmployeeAccess,
    }" />
</ng-template>

<ng-template #grantEmployeeRole>
  <div class="flex justify-center">
    <div
      class="flex justify-center items-center bg-primary-50 p-lg rounded-full mb-xl">
      <lib-tick-square-icon
        iconClass="text-primary-500"
        viewBox="0 0 30 30"
        width="27"
        height="27" />
    </div>
  </div>

  <p class="text-lg text-center font-medium mb-sm text-neutral-900">
    {{ 'users_database_screen_grant_employee_role_modal_title' | transloco }}
  </p>

  <p class="text-sm text-neutral-500 text-center mb-4xl">
    {{
      'users_database_screen_grant_employee_role_modal_description' | transloco
    }}
    <span class="font-medium text-primary-500 block">
      {{
        'users_database_screen_grant_employee_role_modal_highlighted_description'
          | transloco
      }}
    </span>
  </p>

  <div class="flex flex-col gap-md text-base font-medium">
    <button
      (click)="grantEmployeeAccess()"
      class="bg-primary-500 py-sm rounded-md text-white text-base cursor-pointer">
      {{
        'users_database_screen_grant_employee_role_modal_confirm_button'
          | transloco
      }}
    </button>

    <button
      (click)="closeModal()"
      class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
      {{
        'users_database_screen_grant_employee_role_modal_cancel_button'
          | transloco
      }}
    </button>
  </div>
</ng-template>

<ng-template
  #replaceUser
  let-mode
  let-class="class"
  let-header="header"
  let-description="description"
  let-descriptionHighlight="descriptionHighlight"
  let-submitBtnLabel="submitBtnLabel"
  let-submitFn="submitFn">
  <div [formGroup]="deactivateUserFromGroup">
    <div class="flex justify-center">
      <div
        [class]="`flex justify-center items-center bg-${class}-50 p-lg rounded-full mb-xl`">
        @if (mode === REPLACE_USER_MODAL_MODE.DEACTIVATE) {
          <lib-close-square-icon
            viewBox="0 0 30 30"
            width="27"
            height="27"
            iconClass="text-destructive-500" />
        } @else if (mode === REPLACE_USER_MODAL_MODE.CANCEL_INVITATION) {
          <lib-user-cancel-icon
            iconClass="text-primary-500"
            viewBox="0 0 32 32"
            width="27"
            height="27" />
        } @else if (mode === REPLACE_USER_MODAL_MODE.DELETE) {
          <lib-delete-icon [iconClass]="'size-8 text-destructive-500'" />
        } @else if (mode === REPLACE_USER_MODAL_MODE.REMOVE_EMPLOYEE_ROLE) {
          <lib-user-cancel-icon
            iconClass="text-destructive-500"
            viewBox="0 0 32 32"
            width="27"
            height="27" />
        }
      </div>
    </div>

    <p class="text-lg text-center font-medium mb-sm text-neutral-900">
      {{ header }}
    </p>

    <p class="text-sm text-neutral-500 text-center mb-4xl">
      {{ description }}

      @if (descriptionHighlight) {
        <span class="font-medium text-primary-500">
          {{ descriptionHighlight }}
        </span>
      }
    </p>

    @if (
      mode === REPLACE_USER_MODAL_MODE.CANCEL_INVITATION ||
      mode === REPLACE_USER_MODAL_MODE.DEACTIVATE ||
      mode === REPLACE_USER_MODAL_MODE.DELETE ||
      mode === REPLACE_USER_MODAL_MODE.REMOVE_EMPLOYEE_ROLE
    ) {
      <div class="my-4xl">
        <lib-paginated-select
          [id]="'replacementUserId'"
          [inputFormControlName]="'replacementUserId'"
          [name]="'replacementUserId'"
          [label]="'deactivate_user_modal_select_employee_label' | transloco"
          [required]="true"
          [placeholder]="
            'deactivate_user_modal_select_employee_placeholder' | transloco
          "
          [classList]="['text-sm']"
          [apiUrl]="userLookupListApi"
          [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
        <lib-validation-message
          [lang]="'current_lang' | transloco"
          control="replacementUserId" />
      </div>
    }

    <div class="flex flex-col gap-md text-base font-medium">
      @let isSubmitBtnDisabled =
        (mode === REPLACE_USER_MODAL_MODE.CANCEL_INVITATION &&
          deactivateUserFromGroup.invalid) ||
        (mode === REPLACE_USER_MODAL_MODE.DEACTIVATE &&
          deactivateUserFromGroup.invalid) ||
        (mode === REPLACE_USER_MODAL_MODE.DELETE &&
          deactivateUserFromGroup.invalid) ||
        (mode === REPLACE_USER_MODAL_MODE.REMOVE_EMPLOYEE_ROLE &&
          deactivateUserFromGroup.invalid) ||
        isSubmitting();
      <button
        [disabled]="isSubmitBtnDisabled"
        [class.opacity-30]="isSubmitBtnDisabled"
        (click)="submitFn()"
        [class]="`bg-${class}-500 py-sm rounded-md text-white text-base cursor-pointer`">
        {{ submitBtnLabel }}
      </button>

      <button
        [disabled]="isSubmitting()"
        [class.opacity-30]="isSubmitting()"
        (click)="closeModal()"
        class="text-neutral-700 bg-white border border-neutral-300 py-sm rounded-md text-base cursor-pointer">
        {{ 'deactivate_user_modal_no_keep_it_button' | transloco }}
      </button>
    </div>
  </div>
</ng-template>
